/**
 * EL GUION DEL RECORRIDO GUIADO.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ EL RECORRIDO ES ASÍ DE CORTO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Un tutorial de veinte pasos no lo termina nadie. Este enseña **lo que hay que
 * hacer el primer día** y una cosa más: que existe lo demás. Nada de recorrer
 * pantalla por pantalla — eso se descubre solo cuando hace falta.
 *
 * ⚠️ Y hay una regla que evita que vuelva a crecer: **aquí solo entra lo que se
 * puede SEÑALAR**. Si un paso no apunta a ningún botón —"si se cae el internet
 * sigues vendiendo", "las devoluciones se registran solas"— no es un recorrido,
 * es un folleto encima de la pantalla. Eso ya se cuenta donde corresponde, en el
 * paso 2 del wizard de bienvenida (`features/onboarding/BienvenidaPage.tsx`),
 * que sí es un sitio para leer.
 *
 * La única excepción es el último paso, el de Premium, y está justificada
 * abajo.
 *
 * Cada paso apunta a un elemento real con `data-tour`. Si ese elemento no está
 * (el catálogo vacío no dibuja la grilla, por ejemplo), el paso NO se rompe: se
 * enseña centrado. Un tutorial que se cuelga es peor que ninguno.
 *
 * ⚠️ `objetivo` solo puede valer uno de los `data-tour` que existen de verdad en
 * las pantallas: `buscador`, `grilla`, `carrito`, `tasas`, `caja`, `inventario`,
 * `reportes`. Inventarse uno no da error — el paso simplemente sale centrado,
 * en silencio, y nadie se entera de que apuntaba a la nada.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  EL ÚLTIMO PASO ES PREMIUM, Y ES A PROPÓSITO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La prueba gratis es Estándar (ver `backend/app/core/planes.py`), así que quien
 * hace este recorrido **nunca va a tropezarse por su cuenta** con el resumen de
 * WhatsApp ni con el análisis de márgenes. O se los enseñamos aquí, o los
 * descubre dentro de tres semanas al chocarse con un candado — que es el peor
 * momento posible, porque está intentando hacer algo y lo que recibe es un no.
 */

export type PasoTutorial = {
  /** A qué pantalla hay que ir antes de mostrarlo. */
  ruta: string;
  /**
   * El `data-tour` del elemento a señalar. Si falta, o si el elemento no está
   * en pantalla, el paso se muestra centrado.
   */
  objetivo?: string;
  titulo: string;
  texto: string;
  /** Permiso necesario. Un empleado no ve los pasos de reportes. */
  permiso?: string;
  /** Solo se muestra si el negocio tiene más de una moneda. */
  soloMultimoneda?: boolean;
};

export const PASOS: PasoTutorial[] = [
  {
    ruta: "/vender",
    titulo: "Empecemos por lo importante",
    texto:
      "Te muestro Softfi en un minuto. Puedes salirte cuando quieras — el recorrido queda guardado para retomarlo, y está siempre en Ajustes.",
  },

  // ── VENDER ────────────────────────────────────────────────────────────────
  {
    ruta: "/vender",
    objetivo: "buscador",
    titulo: "Aquí entra cada venta",
    texto:
      "Escribe el nombre del producto, o escanea su código de barras. Un lector USB funciona como un teclado: escanea y el producto cae solo en el carrito, sin tocar nada.",
  },
  {
    ruta: "/vender",
    objetivo: "grilla",
    titulo: "O toca el producto",
    texto:
      "El precio es lo más grande de cada tarjeta a propósito: en el mostrador la pregunta siempre es «¿cuánto cuesta?». Debajo verás el equivalente en tu moneda de cobro.",
  },
  {
    ruta: "/vender",
    objetivo: "carrito",
    titulo: "Cobrar, y el carrito en espera",
    texto:
      "Al cobrar puedes dividir el pago entre varias monedas y métodos, y dar el vuelto en la que quieras. Y si el cliente se fue a buscar algo más, «Guardar carrito» te lo deja apartado para atender al siguiente.",
  },

  // ── LA TASA ───────────────────────────────────────────────────────────────
  {
    ruta: "/monedas",
    objetivo: "tasas",
    titulo: "La tasa del día",
    texto:
      "Esto es lo único que hay que hacer cada mañana: escribir la tasa de hoy. Todos tus precios se actualizan solos — no tienes que tocar el catálogo nunca.",
    permiso: "currency.view",
    soloMultimoneda: true,
  },

  // ── CAJA E INVENTARIO ─────────────────────────────────────────────────────
  {
    ruta: "/caja",
    objetivo: "caja",
    titulo: "Abre la caja al empezar",
    texto:
      "Declara el sencillo que dejas para dar vuelto. Si no lo declaras, al cerrar parecerá que sobró dinero. Al final del día cuentas los billetes y Softfi te dice si cuadra — moneda por moneda.",
    permiso: "cash.open",
  },
  {
    ruta: "/inventario",
    objetivo: "inventario",
    titulo: "Qué tienes y qué falta",
    texto:
      "El botón «Solo lo que falta» te deja la lista de la compra hecha. Cuando llegue mercancía, regístrala en Entrada con su costo: es lo que le permite a Softfi decirte tu ganancia real.",
    permiso: "inventory.view",
  },

  // ── REPORTES ──────────────────────────────────────────────────────────────
  {
    ruta: "/reportes",
    objetivo: "reportes",
    titulo: "Cuánto ganaste de verdad",
    texto:
      "No solo cuánto vendiste. Softfi resta el costo de cada producto, descuenta el impuesto (que no es tuyo) y te compara con el período anterior. Si algo no tiene costo cargado, te avisa de que la ganancia que ves está inflada.",
    permiso: "reports.view",
  },

  // ── PREMIUM ───────────────────────────────────────────────────────────────
  {
    ruta: "/premium",
    titulo: "Y cuando el negocio crezca",
    texto:
      "Premium te manda cada noche por WhatsApp cuánto vendiste y cuánto ganaste, te avisa al momento si anulan una venta, y te dice a qué producto le subió el costo sin que le subieras el precio. Aquí lo tienes todo comparado.",
    permiso: "tenant.manage",
  },

  {
    ruta: "/",
    titulo: "Ya está, eso es todo",
    texto:
      "Vender, cobrar, cuadrar la caja y ver cuánto ganaste. Lo demás lo irás descubriendo. Puedes repetir este recorrido cuando quieras desde Ajustes.",
  },
];
